import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  IconButton,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  Science as ScienceIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '@/services/api';

interface OptimizationResultsProps {
  testMode?: boolean;
}

// Sample interface for mock data structure
interface OptimizationData {
  id: string;
  status: string;
  created_at: string;
  updated_at: string;
  pdb_file: string;
  wild_type_melting_temp: number | null;
  target_melting_temp: number | null;
  results: {
    sequences?: Array<{
      sequence: string;
      name: string;
      predicted_temp: number;
      stability_score: number;
    }>;
  };
  error_message: string | null;
}

// Mock data for test mode
const mockOptimizationData: OptimizationData = {
  id: 'test-123',
  status: 'completed',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  pdb_file: 'test.pdb',
  wild_type_melting_temp: 45.2,
  target_melting_temp: 55.0,
  results: {
    sequences: Array.from({ length: 20 }, (_, i) => ({
      sequence: 'M' + 'A'.repeat(50) + 'K', // Dummy sequence
      name: `Optimized Sequence ${i + 1}`,
      predicted_temp: 55.0 + (Math.random() * 2 - 1), // Random temp around target
      stability_score: 0.8 + Math.random() * 0.2, // Random stability score
    })),
  },
  error_message: null,
};

const OptimizationResults: React.FC<OptimizationResultsProps> = ({ testMode = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [optimization, setOptimization] = useState<OptimizationData | null>(testMode ? mockOptimizationData : null);
  const [loading, setLoading] = useState(!testMode);
  const [error, setError] = useState<string | null>(null);
  
  const fetchData = useCallback(async () => {
    if (testMode) return;
    
    try {
      setLoading(true);
      
      // Fetch optimization data from API
      const response = await api.get(`/api/optimize/${id}/`);
      setOptimization(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching optimization data:', error);
      setError('Failed to load optimization data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id, testMode]);
  
  useEffect(() => {
    fetchData();
    
    // Create a reference to the current optimization status to avoid re-creating intervals
    const currentStatus = optimization?.status;
    
    // Only set up polling for pending or processing status
    const shouldPoll = !testMode && currentStatus && ['pending', 'processing'].includes(currentStatus);
    
    let interval: NodeJS.Timeout | null = null;
    
    if (shouldPoll) {
      console.log(`Setting up polling interval for optimization ${id} with status ${currentStatus}`);
      interval = setInterval(fetchData, 10000);
    }
    
    return () => {
      if (interval) {
        console.log(`Clearing polling interval for optimization ${id}`);
        clearInterval(interval);
      }
    };
  }, [fetchData, testMode, optimization?.status, id]);
  
  const handleDownloadResults = () => {
    if (!optimization || !optimization.results.sequences) return;
    
    // Create CSV content
    let csvContent = "Sequence Name,Sequence,Predicted Temperature (°C),Stability Score\n";
    
    optimization.results.sequences.forEach(seq => {
      csvContent += `"${seq.name}","${seq.sequence}",${seq.predicted_temp},${seq.stability_score}\n`;
    });
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `protein-optimization-${id}.csv`);
    a.click();
  };
  
  const handleSubmitForValidation = () => {
    // This would typically navigate to a validation/ordering page
    // For now, just show an alert
    alert('This feature is coming soon! This would allow you to submit these sequences for laboratory validation.');
  };
  
  if (loading && !optimization) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
          <Box mt={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/jobs')}
            >
              Back to Jobs
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }
  
  if (!optimization) {
    return (
      <Container maxWidth="lg">
        <Box mt={4}>
          <Alert severity="warning">
            <AlertTitle>Not Found</AlertTitle>
            The requested optimization could not be found.
          </Alert>
          <Box mt={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/jobs')}
            >
              Back to Jobs
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box mb={4} mt={3}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={() => navigate('/jobs')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Protein Optimization Results
          </Typography>
          {!testMode && (
            <IconButton 
              sx={{ ml: 2 }}
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          )}
        </Box>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Optimization Details
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip 
                  label={optimization.status} 
                  color={
                    optimization.status === 'completed' ? 'success' :
                    optimization.status === 'failed' ? 'error' :
                    optimization.status === 'processing' ? 'info' : 'default'
                  }
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">Submitted</Typography>
                <Typography variant="body1">
                  {new Date(optimization.created_at).toLocaleString()}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body1">
                  {new Date(optimization.updated_at).toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Temperature Settings
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">Wild Type Melting Temperature</Typography>
                <Typography variant="body1">
                  {optimization.wild_type_melting_temp !== null 
                    ? `${optimization.wild_type_melting_temp} °C` 
                    : 'Not provided'}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">Target Melting Temperature</Typography>
                <Typography variant="body1">
                  {optimization.target_melting_temp !== null 
                    ? `${optimization.target_melting_temp} °C` 
                    : 'Not provided'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {['pending', 'processing'].includes(optimization.status) && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center" p={3}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">
                Your optimization is in progress
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center">
                We're analyzing your protein and generating optimized sequences. 
                This process may take some time. 
                You'll be notified when your results are ready.
              </Typography>
            </Box>
          </Paper>
        )}
        
        {optimization.status === 'failed' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Alert severity="error">
              <AlertTitle>Optimization Failed</AlertTitle>
              {optimization.error_message || 'An error occurred during optimization. Please try again or contact support.'}
            </Alert>
          </Paper>
        )}
        
        {optimization.status === 'completed' && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5">
                Optimized Sequences
              </Typography>
              <Box>
                <Button 
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadResults}
                  sx={{ mr: 2 }}
                >
                  Download CSV
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSubmitForValidation}
                  startIcon={<ScienceIcon />}
                >
                  Submit for Validation
                </Button>
              </Box>
            </Box>
            
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sequence Name</TableCell>
                      <TableCell>Sequence</TableCell>
                      <TableCell align="right">Predicted Temperature (°C)</TableCell>
                      <TableCell align="right">Stability Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {optimization.results.sequences ? (
                      optimization.results.sequences.map((seq, index) => (
                        <TableRow key={index}>
                          <TableCell>{seq.name}</TableCell>
                          <TableCell>
                            <Tooltip title={seq.sequence}>
                              <Typography sx={{ 
                                maxWidth: 300, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap' 
                              }}>
                                {seq.sequence}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{seq.predicted_temp.toFixed(1)}</TableCell>
                          <TableCell align="right">{seq.stability_score.toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No sequences available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default OptimizationResults; 